/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, April 3rd 2022, 4:01:47 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { PrimaryKeyValue, ObjectValue, UUID4 } from "lib/types";

export enum LIST_EDITOR_ACTIONS {
  CREATE_ITEM,
  DEQUEUE_NEW_ITEM,
  UPDATE_LIST,
  REMOVE_ITEM,
  HANDLE_DRAG_ENTER,
  SELECT_ITEM,
  LOAD_ITEMS,
  FILTER_LIST,
}

export type CreationQueueItem<T> = T & { os_unsaved_item_temp_unique_id: UUID4 };

export type ItemsListReducerAction<T> =
  | CreateItemAction<T>
  | DequeueNewItemAction<T>
  | UpdateListAction<T>
  | SelectItemAction<T>
  | DeleteItemAction<T>
  | LoadItemsListAction<T>
  | FilterListAction<T>
  | ItemDragEnterAction<T>;

export type ItemsListFilterFunction<T> = (items: T[]) => PrimaryKeyValue<T>[] | null;

interface CreateItemAction<T> {
  actionType: LIST_EDITOR_ACTIONS.CREATE_ITEM;
  payload: { newItem: T };
}

interface DequeueNewItemAction<T> {
  actionType: LIST_EDITOR_ACTIONS.DEQUEUE_NEW_ITEM;
  payload: { newItemTempId: CreationQueueItem<T>["os_unsaved_item_temp_unique_id"] };
}

export interface UpdateListOptions<T> {
  newItemToRemoveTempId: CreationQueueItem<T>["os_unsaved_item_temp_unique_id"];
}

interface UpdateListAction<T> {
  actionType: LIST_EDITOR_ACTIONS.UPDATE_LIST;
  payload: { itemToUpdate: T; options?: UpdateListOptions<T> };
}

interface SelectItemAction<T> {
  actionType: LIST_EDITOR_ACTIONS.SELECT_ITEM;
  payload: { selectedItemId: PrimaryKeyValue<T> };
}

interface DeleteItemAction<T> {
  actionType: LIST_EDITOR_ACTIONS.REMOVE_ITEM;
  payload: { itemToRemoveId: PrimaryKeyValue<T> };
}

interface LoadItemsListAction<T> {
  actionType: LIST_EDITOR_ACTIONS.LOAD_ITEMS;
  payload: { fetchedItems: T[] };
}

interface FilterListAction<T> {
  actionType: LIST_EDITOR_ACTIONS.FILTER_LIST;
  listFilterFunction: ItemsListFilterFunction<T>;
}

// Note: For ItemDragEnterAction payload we use the hoverPosition as opposed to hoverItemId so we can be able
// to cancel a drop by storing the initial drop position and doing a drag enter back to that position in case
// the use drops on an invalid target (cancels)
interface ItemDragEnterAction<T> {
  actionType: LIST_EDITOR_ACTIONS.HANDLE_DRAG_ENTER;
  payload: { sourceItemId: PrimaryKeyValue<T>; hoverPosition: ObjectValue<T> & number };
}
